import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Palette, Save, Ban, CalendarClock, Timer, Ticket, RotateCcw } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { refreshSeasonal } from '../lib/useSeasonal';
import { THEMES } from '../lib/seasonalThemes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'w-full bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-md px-3 py-2 text-[13.5px] outline-none focus:border-neutral-400';
const box = 'rounded-lg bg-neutral-50 dark:bg-neutral-900/50 p-4 space-y-3';
const AUTO = { label: 'Automatic (by dates)', icon: CalendarClock, swatch: ['#059669', '#0f172a'] };
const fmtDay = (d) => (d ? new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const Field = ({ label, children }) => (
  <label className="block"><span className="block text-[11px] tracking-widest uppercase text-neutral-500 mb-1">{label}</span>{children}</label>
);

const Toggle = ({ checked, onChange, label, testId }) => (
  <label className="inline-flex items-center gap-2 text-[13px] cursor-pointer">
    <input data-testid={testId} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-emerald-600" /> {label}
  </label>
);

const ThemeTile = ({ k, active, editing, onPick, liveNow }) => {
  const t = k === 'auto' ? AUTO : THEMES[k]; const I = t ? t.icon : Ban;
  return (
    <button type="button" data-testid={`seasonal-theme-${k}`} onClick={onPick}
      className={`relative rounded-lg border p-3 text-left transition-colors ${active ? 'border-transparent ring-2 ring-emerald-600' : editing ? 'border-neutral-400 dark:border-neutral-500' : 'border-neutral-200 dark:border-neutral-800 hover:border-neutral-400'}`}>
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 rounded-md grid place-items-center text-white shrink-0" style={{ background: t ? `linear-gradient(135deg, ${t.swatch[0]}, ${t.swatch[1]})` : '#a3a3a3' }}><I size={15} /></span>
        <span className="text-[13px] font-medium text-neutral-900 dark:text-neutral-100">{t ? t.label : 'No theme'}</span>
      </div>
      {active && <span className="absolute top-1.5 right-2 text-[9.5px] font-bold tracking-widest uppercase text-emerald-600">{k === 'auto' ? 'On' : 'Live'}</span>}
      {liveNow && <div data-testid="seasonal-auto-now" className="text-[10.5px] text-neutral-500 mt-1.5">Showing now: {liveNow}</div>}
    </button>
  );
};

const ScheduleBox = ({ c, onChange }) => {
  const sch = c.schedule || {};
  const set = (f, v) => onChange({ ...c, schedule: { ...sch, [f]: v, custom: true } });
  return (
    <div data-testid="seasonal-schedule" className={box}>
      <div className="flex flex-wrap items-center gap-2 text-[13px] font-medium"><CalendarClock size={14} className="text-brand" /> Dates <span className="text-[11.5px] font-normal text-neutral-500">used by Automatic mode, the countdown and the coupon expiry</span></div>
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Starts"><input data-testid="seasonal-schedule-start" type="date" className={inputCls + ' w-44'} value={sch.start || ''} onChange={(e) => set('start', e.target.value)} /></Field>
        <Field label="Ends (last day)"><input data-testid="seasonal-schedule-end" type="date" className={inputCls + ' w-44'} value={sch.end || ''} onChange={(e) => set('end', e.target.value)} /></Field>
        {sch.custom
          ? <button type="button" data-testid="seasonal-schedule-reset" onClick={() => onChange({ ...c, schedule: { ...c.default_schedule, custom: false } })} className="inline-flex items-center gap-1 text-[12px] text-neutral-600 dark:text-neutral-300 hover:text-brand pb-2.5"><RotateCcw size={12} /> Use standard dates</button>
          : <span className="text-[11.5px] text-neutral-500 pb-2.5">Standard dates — move to next year automatically</span>}
      </div>
      <Toggle testId="seasonal-countdown-toggle" checked={!!c.countdown} onChange={(v) => onChange({ ...c, countdown: v })}
        label={<span className="inline-flex items-center gap-1"><Timer size={13} /> Show a countdown on the banner until the end of {fmtDay(sch.end)}</span>} />
    </div>
  );
};

const CouponMaker = ({ k, c, headers, onMade }) => {
  const [code, setCode] = useState(c.banner.code || `${THEMES[k].label.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 10)}10`);
  const [pct, setPct] = useState(10);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ text: '', err: false });
  const make = async () => {
    setBusy(true); setMsg({ text: '', err: false });
    try {
      const { data } = await axios.post(`${API}/admin/settings/seasonal/${k}/coupon`, { code, percent: Number(pct) }, { headers });
      onMade(data.coupon.code);
      setMsg({ text: `Coupon ${data.coupon.code} created: ${data.coupon.value}% off, ends ${fmtDay(data.coupon.expires_at)}. It's now shown on the banner.`, err: false });
    } catch (e) { setMsg({ text: e?.response?.data?.detail || 'Could not create coupon', err: true }); } finally { setBusy(false); }
  };
  return (
    <div data-testid="seasonal-coupon-maker" className="rounded-lg border border-dashed border-neutral-300 dark:border-neutral-700 p-4 space-y-2">
      <div className="flex items-center gap-2 text-[13px] font-medium"><Ticket size={14} className="text-brand" /> One-tap holiday coupon</div>
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Code"><input data-testid="seasonal-coupon-code" className={inputCls + ' font-mono w-44'} value={code} maxLength={32} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))} /></Field>
        <Field label="% off"><input data-testid="seasonal-coupon-percent" type="number" min="1" max="100" className={inputCls + ' w-24'} value={pct} onChange={(e) => setPct(e.target.value)} /></Field>
        <button type="button" data-testid="seasonal-coupon-create" disabled={busy || code.length < 3 || !(Number(pct) > 0)} onClick={make}
          className="btn-brand rounded-md px-4 py-2 text-[13px] font-semibold disabled:opacity-50">{busy ? 'Creating…' : 'Create coupon'}</button>
      </div>
      <p className="text-[11.5px] text-neutral-500">Expires automatically at the end of {fmtDay(c.schedule?.end)} (save new dates first). Manage it later in the Coupons tab.</p>
      {msg.text && <div data-testid="seasonal-coupon-msg" className={`text-[12px] ${msg.err ? 'text-red-600' : 'text-emerald-600'}`}>{msg.text}</div>}
    </div>
  );
};

const BannerFields = ({ k, c, setB }) => (
  <div className={box}>
    <Toggle testId="seasonal-banner-toggle" checked={c.banner.enabled} onChange={(v) => setB('enabled', v)} label={`${THEMES[k].label} homepage banner`} />
    <div className="grid sm:grid-cols-2 gap-3">
      <Field label="Small heading"><input data-testid="seasonal-banner-eyebrow" className={inputCls} value={c.banner.eyebrow} onChange={(e) => setB('eyebrow', e.target.value)} maxLength={40} /></Field>
      <Field label="Coupon code shown (optional)"><input data-testid="seasonal-banner-code" className={inputCls + ' font-mono'} value={c.banner.code} onChange={(e) => setB('code', e.target.value.toUpperCase())} maxLength={32} /></Field>
      <Field label="Title"><input data-testid="seasonal-banner-title" className={inputCls} value={c.banner.title} onChange={(e) => setB('title', e.target.value)} maxLength={80} /></Field>
      <Field label="Subtitle"><input data-testid="seasonal-banner-subtitle" className={inputCls} value={c.banner.subtitle} onChange={(e) => setB('subtitle', e.target.value)} maxLength={200} /></Field>
      <Field label="Button text"><input data-testid="seasonal-banner-cta-label" className={inputCls} value={c.banner.cta_label} onChange={(e) => setB('cta_label', e.target.value)} maxLength={30} /></Field>
      <Field label="Button link"><input data-testid="seasonal-banner-cta-link" className={inputCls} value={c.banner.cta_link} onChange={(e) => setB('cta_link', e.target.value)} placeholder="/#catalog" /></Field>
    </div>
  </div>
);

const AnnFields = ({ c, setA }) => (
  <div className={box}>
    <Toggle testId="seasonal-ann-toggle" checked={c.announcement.enabled} onChange={(v) => setA('enabled', v)} label="Show this message first in the announcement bar" />
    <div className="grid sm:grid-cols-[2fr_1fr] gap-3">
      <Field label="Message"><input data-testid="seasonal-ann-text" className={inputCls} value={c.announcement.text} onChange={(e) => setA('text', e.target.value)} maxLength={120} /></Field>
      <Field label="Link (optional)"><input data-testid="seasonal-ann-link" className={inputCls} value={c.announcement.link} onChange={(e) => setA('link', e.target.value)} placeholder="/page/holiday-sale" /></Field>
    </div>
  </div>
);

export const SeasonalEditor = () => {
  const { password } = useAdminAuth();
  const headers = useMemo(() => ({ 'X-Admin-Password': password }), [password]);
  const [s, setS] = useState(null);
  const [edit, setEdit] = useState('halloween');
  const [msg, setMsg] = useState({ text: '', err: false });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/settings/seasonal`).then(({ data }) => { setS(data); if (THEMES[data.theme]) setEdit(data.theme); })
      .catch(() => setMsg({ text: 'Could not load', err: true }));
  }, []);
  if (!s) return null;
  const c = s.content[edit];
  const setC = (v) => setS(prev => ({ ...prev, content: { ...prev.content, [edit]: v } }));
  const pick = (k) => { setS({ ...s, mode: k }); if (THEMES[k]) setEdit(k); };
  const save = async () => {
    setSaving(true); setMsg({ text: '', err: false });
    try {
      const { data } = await axios.put(`${API}/admin/settings/seasonal`, { theme: s.mode, effects: s.effects, content: s.content }, { headers });
      setS(data); refreshSeasonal(); setMsg({ text: 'Saved — the storefront updates on next page load.', err: false });
    } catch (e) { setMsg({ text: e?.response?.data?.detail || 'Save failed', err: true }); } finally { setSaving(false); }
  };
  return (
    <section data-testid="seasonal-editor" className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] p-5 space-y-4">
      <div>
        <h3 className="text-[15px] font-semibold text-neutral-900 dark:text-neutral-100 inline-flex items-center gap-2"><Palette size={16} className="text-brand" /> Holiday themes</h3>
        <p className="text-[12.5px] text-neutral-500">Choose Automatic to switch themes on their dates, or pick one by hand (your pick wins). Each theme changes the colours and adds effects, a homepage banner and an announcement.</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        <ThemeTile k="none" active={s.mode === 'none'} onPick={() => pick('none')} />
        <ThemeTile k="auto" active={s.mode === 'auto'} onPick={() => pick('auto')} liveNow={s.mode === 'auto' ? (THEMES[s.theme]?.label || 'no holiday today') : ''} />
        {Object.keys(THEMES).map(k => <ThemeTile key={k} k={k} active={s.mode === k} editing={edit === k && s.mode !== k} onPick={() => pick(k)} />)}
      </div>
      <Toggle testId="seasonal-effects-toggle" checked={s.effects} onChange={(v) => setS({ ...s, effects: v })} label="Show animated effects (bats, snow, confetti, stars, petals, leaves)" />
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <span className="text-[12px] text-neutral-500">Edit settings for:</span>
        <select data-testid="seasonal-edit-select" value={edit} onChange={(e) => setEdit(e.target.value)} className={inputCls + ' w-auto py-1.5'}>
          {Object.entries(THEMES).map(([k, t]) => <option key={k} value={k}>{t.label} · {fmtDay(s.content[k].schedule?.start)} – {fmtDay(s.content[k].schedule?.end)}{s.theme === k ? ' (live)' : ''}</option>)}
        </select>
      </div>
      <div data-testid={`seasonal-content-${edit}`} className="space-y-3">
        <ScheduleBox c={c} onChange={setC} />
        <CouponMaker key={`cm-${edit}`} k={edit} c={c} headers={headers} onMade={(code) => setC({ ...c, banner: { ...c.banner, code } })} />
        <BannerFields k={edit} c={c} setB={(f, v) => setC({ ...c, banner: { ...c.banner, [f]: v } })} />
        <AnnFields c={c} setA={(f, v) => setC({ ...c, announcement: { ...c.announcement, [f]: v } })} />
      </div>
      <div className="flex items-center gap-3">
        <button data-testid="seasonal-save-btn" onClick={save} disabled={saving} className="btn-brand inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-[13px] font-semibold disabled:opacity-50"><Save size={14} /> {saving ? 'Saving…' : 'Save'}</button>
        {msg.text && <span data-testid="seasonal-msg" className={`text-[12.5px] ${msg.err ? 'text-red-600' : 'text-emerald-600'}`}>{msg.text}</span>}
      </div>
    </section>
  );
};
